import type { InjectionKey, Plugin } from 'vue';
import type { Router } from 'vue-router';

import { AppTrackingContext, Tracker } from './Tracker';
import {
  ClickTrackingListener,
  TrackPayloadResolver,
  TrackRegistry,
  TrackScopeRegistry,
  createTrackDirective,
  createTrackScopeDirective,
} from './trackDirective';
import type { TrackEvent } from './events';
import type { TrackingDestination } from './destinations/TrackingDestination';

export const TRACKER_KEY: InjectionKey<Tracker> = Symbol('tracker');
export type TrackFn = (value: TrackEvent, origin?: Node | null) => void;

interface AnalyticsOptions {
  router: Router;
  destinations: readonly TrackingDestination[];
}

export function createAnalytics({
  router,
  destinations,
}: AnalyticsOptions): Plugin {
  return {
    install(app) {
      const tracker = new Tracker(new AppTrackingContext(router), destinations);
      const registry = new TrackRegistry();
      const scopes = new TrackScopeRegistry();
      const resolver = new TrackPayloadResolver(scopes);

      new ClickTrackingListener(registry, resolver, tracker).attach();

      const track: TrackFn = (value, origin = null) =>
        tracker.track(value.event, resolver.resolve(value, origin));

      app.directive('track', createTrackDirective(registry));
      app.directive('track-scope', createTrackScopeDirective(scopes));
      app.provide(TRACKER_KEY, tracker);
      app.config.globalProperties.$track = track;
    },
  };
}
